/**
 * Cart Session Adapter — Injectable contract for server-side cart ID persistence.
 *
 * module-quote uses this to read/write cart IDs from the visitor session (Redis)
 * without depending on packages/app code.
 *
 * App layer injects the concrete implementation (visitorSessionClient) at bootstrap.
 *
 * @see storeCodeEmitter.CookieAdapter in module-core for the same injectable pattern.
 *
 * USAGE:
 * ```typescript
 * // 1. App bootstrap — inject visitorSessionClient (in _app.tsx or provider)
 * import { cartSessionManager } from '@kidslabs/module-quote';
 * import { visitorSessionClient } from '@/lib/visitor-session';
 *
 * cartSessionManager.setAdapter(visitorSessionClient);
 *
 * // 2. Use in module-quote hooks/providers
 * const sessionCart = await cartSessionManager.getCartId(storeCode);
 * await cartSessionManager.setCartId(storeCode, cartId);
 * await cartSessionManager.clearCartId(storeCode);
 * ```
 */

// ============================================================================
// Types
// ============================================================================

export interface CartSessionResponse {
    cartId: string | null;
    pendingGuestCartId: string | null;
    /** True if auth context changed on server and session carts were cleared.
     *  Client MUST clear all local cart data (Legend-State, IndexedDB). */
    authContextChanged?: boolean;
}

/**
 * Injectable adapter interface for cart session persistence.
 * The app layer provides a concrete implementation (e.g., visitorSessionClient)
 * that communicates with the server-side session store (Redis).
 */
export interface CartSessionAdapter {
    getCartId(storeCode: string): Promise<CartSessionResponse>;
    setCartId(storeCode: string, cartId: string): Promise<boolean>;
    clearCartId(storeCode: string): Promise<boolean>;
}

// ============================================================================
// Cart Session Manager (Singleton)
// ============================================================================

class CartSessionManager {
    private adapter: CartSessionAdapter | null = null;

    /**
     * Inject the concrete adapter implementation.
     * Should be called once at app startup.
     *
     * @example
     * ```typescript
     * import { visitorSessionClient } from '@/lib/visitor-session';
     * cartSessionManager.setAdapter(visitorSessionClient);
     * ```
     */
    public setAdapter(adapter: CartSessionAdapter): void {
        this.adapter = adapter;
        console.log('[CartSessionManager] Adapter injected');
    }

    /**
     * Check if an adapter has been injected.
     * Useful for graceful degradation when running without session support.
     */
    public hasAdapter(): boolean {
        return this.adapter !== null;
    }

    /**
     * Get the cart ID for a specific store code from the server session.
     * Returns `{ cartId: null, pendingGuestCartId: null }` if no adapter is set.
     */
    public async getCartId(storeCode: string): Promise<CartSessionResponse> {
        if (!this.adapter) {
            console.warn('[CartSessionManager] No adapter set — returning empty');
            return { cartId: null, pendingGuestCartId: null };
        }
        return this.adapter.getCartId(storeCode);
    }

    /**
     * Persist a cart ID for a specific store code to the server session.
     * Returns `false` if no adapter is set.
     */
    public async setCartId(storeCode: string, cartId: string): Promise<boolean> {
        if (!this.adapter) {
            console.warn('[CartSessionManager] No adapter set — cannot persist cart ID');
            return false;
        }
        if (!storeCode || !cartId) {
            console.warn('[CartSessionManager] Missing storeCode or cartId — skipping setCartId', {
                storeCode: storeCode || '(empty)',
                cartId: cartId || '(empty)',
            });
            return false;
        }
        return this.adapter.setCartId(storeCode, cartId);
    }

    /**
     * Clear the cart ID for a specific store code from the server session.
     * Returns `false` if no adapter is set.
     */
    public async clearCartId(storeCode: string): Promise<boolean> {
        if (!this.adapter) {
            console.warn('[CartSessionManager] No adapter set — cannot clear cart ID');
            return false;
        }
        if (!storeCode) {
            console.warn('[CartSessionManager] Missing storeCode — skipping clearCartId');
            return false;
        }
        return this.adapter.clearCartId(storeCode);
    }
}

// Export singleton instance
export const cartSessionManager = new CartSessionManager();

// Export class for testing
export { CartSessionManager };
